<template>
  <div class="mctb_carousel_01_ mctb-carousel">
    <div class="mctb_carousel_02_ mctb-carousel-mctbwrapper">
      <div
        class="mctb_carousel_03_ mctb-carousel-mctbinner"
        :style="`
          transform: translateX(${mctbcurrentOffset}px);
          transition: ${transitionStyle};
          flex-basis: ${slideWidth}px;
          visibility: ${slideWidth ? 'visible' : 'hidden'}`"
      >
        <slot></slot>
      </div>
      <pagination v-if="paginationEnabled && mctbpageCount > 0"></pagination>
    </div>
    <navigation v-if="navigationEnabled"></navigation>
  </div>
</template>

<script>
import Navigation from './mctb-navigation.vue';
import Pagination from './mctb-pagination.vue';

export default {
  name: 'mctb-carousel',
  components: {
    Navigation,
    Pagination,
  },
  props: {
    easing: {
      type: String,
      default: 'ease',
    },
    minSwipeDistance: {
      type: Number,
      default: 8,
    },
    navigationEnabled: {
      type: Boolean,
      default: true,
    },
    perPage: {
      type: Number,
      default: 1,
    },
    perPageCustom: {
      type: Array,
    },
    scrollPerPage: {
      type: Boolean,
      default: false,
    },
    speed: {
      type: Number,
      default: 500,
    },
    loop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      browserWidth: null,
      carouselWidth: null,
      currentPage: 0,
      dragOffset: 0,
      dragStartX: 0,
      mousedown: false,
      slideCount: 0,
    };
  },
  computed: {
    mctbbreakpointSlidesPerPage() {
      if (!this.perPageCustom) {
        return this.perPage;
      }

      const breakpointArray = this.perPageCustom;
      const width = this.browserWidth;

      const breakpoints = breakpointArray.sort((a, b) => (a[0] > b[0] ? -1 : 1));
      const matches = breakpoints.filter((breakpoint) => width >= breakpoint[0]);

      const match = matches[0] && matches[0][1];

      return match || this.perPage;
    },
    mctbcanAdvanceForward() {
      return this.loop || this.currentPage < this.mctbpageCount - 1;
    },
    mctbcanAdvanceBackward() {
      return this.loop || this.currentPage > 0;
    },
    mctbcurrentPerPage() {
      return !this.perPageCustom || this.$isServer
        ? this.perPage
        : this.mctbbreakpointSlidesPerPage;
    },
    mctbcurrentOffset() {
      const page = this.currentPage;
      const width = this.slideWidth;
      const dragged = this.dragOffset;

      const offset = this.scrollPerPage ? page * width * this.mctbcurrentPerPage : page * width;

      return (offset + dragged) * -1;
    },
    mctbisHidden() {
      return this.carouselWidth <= 0;
    },

    mctbpageCount() {
      const slideCount = this.slideCount;
      const perPage = this.mctbcurrentPerPage;

      if (this.scrollPerPage) {
        const pages = Math.ceil(slideCount / perPage);
        return pages < 1 ? 1 : pages;
      }

      return slideCount - (this.mctbcurrentPerPage - 1);
    },

    slideWidth() {
      const width = this.carouselWidth;
      const perPage = this.mctbcurrentPerPage;

      return width / perPage;
    },
    transitionStyle() {
      return `${this.speed / 1000}s ${this.easing} transform`;
    },
  },
  beforeUpdate() {
    this.mctbcomputeCarouselWidth();
  },
  methods: {
    mctbgetNextPage() {
      if (this.currentPage < this.mctbpageCount - 1) {
        return this.currentPage + 1;
      }
      return this.loop ? 0 : this.currentPage;
    },
    mctbgetPreviousPage() {
      if (this.currentPage > 0) {
        return this.currentPage - 1;
      }
      return this.loop ? this.mctbpageCount - 1 : this.currentPage;
    },
    mctbadvancePage(direction) {
      if (direction && direction === 'backward' && this.mctbcanAdvanceBackward) {
        this.mctbgoToPage(this.mctbgetPreviousPage());
      } else if (
        (!direction || (direction && direction !== 'backward')) &&
        this.mctbcanAdvanceForward
      ) {
        this.mctbgoToPage(this.mctbgetNextPage());
      }
    },
    mctbattachMutationObserver() {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

      if (MutationObserver) {
        const config = { attributes: true, data: true };
        this.mutationObserver = new MutationObserver(() => {
          this.$nextTick(() => {
            this.mctbcomputeCarouselWidth();
          });
        });
        if (this.$parent.$el) {
          this.mutationObserver.observe(this.$parent.$el, config);
        }
      }
    },
    mctbdetachMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },
    mctbgetBrowserWidth() {
      this.browserWidth = window.innerWidth;
      return this.browserWidth;
    },
    mctbgetCarouselWidth() {
      this.carouselWidth = (this.$el && this.$el.clientWidth) || 0;
      return this.carouselWidth;
    },
    mctbgetSlideCount() {
      const defaultSlot = this.$slots.default && this.$slots.default();
      this.slideCount =
        (defaultSlot &&
          defaultSlot.filter((slot) => slot.tag && slot.tag.indexOf('slide') > -1).length) ||
        0;
    },
    mctbgoToPage(page) {
      if (page >= 0 && page <= this.mctbpageCount) {
        this.currentPage = page;
        this.$emit('pageChange', this.currentPage);
        window.eventBus.$emit('global-page-change', this.currentPage);
      }
    },
    mctbhandleMousedown(e) {
      if (!e.touches) {
        e.preventDefault();
      }

      this.mousedown = true;
      this.dragStartX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
    },
    mctbhandleMouseup() {
      this.mousedown = false;
      this.dragOffset = 0;
    },
    mctbhandleMousemove(e) {
      if (!this.mousedown) {
        return;
      }

      const eventPosX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
      const deltaX = this.dragStartX - eventPosX;

      this.dragOffset = deltaX;
      /* eslint-disable */
      if(this.dragOffset > this.minSwipeDistance) {
        this.mctbhandleMouseup()
        this.mctbadvancePage()
      }else if(this.dragOffset < -this.minSwipeDistance) {
        this.mctbhandleMouseup()
        this.mctbadvancePage('backward')
      }
    },
    mctbcomputeCarouselWidth() {
      this.mctbgetSlideCount()
      this.mctbgetBrowserWidth()
      this.mctbgetCarouselWidth()
      this.mctbsetCurrentPageInBounds()
    },
    mctbsetCurrentPageInBounds() {
      if(!this.mctbcanAdvanceForward) {
        const setPage = (this.mctbpageCount - 1)
        this.currentPage = (setPage >= 0) ? setPage : 0
      }
    }
  },
  mounted() {
    if(!this.$isServer) {
      if('ontouchstart' in window) {
        this.$el.addEventListener('touchstart', this.mctbhandleMousedown)
        this.$el.addEventListener('touchend', this.mctbhandleMouseup)
        this.$el.addEventListener('touchmove', this.mctbhandleMousemove)
      }else{
        this.$el.addEventListener('mousedown', this.mctbhandleMousedown)
        this.$el.addEventListener('mouseup', this.mctbhandleMouseup)
        this.$el.addEventListener('mousemove', this.mctbhandleMousemove)
      }
    }

    this.mctbattachMutationObserver()
    this.mctbcomputeCarouselWidth()
  },
  destroyed() {
    if(!this.$isServer) {
      this.mctbdetachMutationObserver()
      window.removeEventListener('resize', this.mctbgetBrowserWidth)
      if('ontouchstart' in window) {
        this.$el.removeEventListener('touchmove', this.mctbhandleMousemove)
      }else{
        this.$el.removeEventListener('mousemove', this.mctbhandleMousemove)
      }
    }
  }
}
</script>
